import React from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import PeopleIcon from '@mui/icons-material/People';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { useMediaQuery } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { AccountData, OrderData, WaitingListItemData } from '../../api';
import { useLanguage } from '../../utils/GeneralUtils';
import { getDateFormat, getTimeFormat } from '../../utils/getDateAndTimeFormat';
import { getExperienceName } from '../utils/passUtils';
import { useManagePasses } from '../ManagePassesContext';

export enum UpcomingEntryType {
  BOOKING = 'booking',
  WAITLIST = 'waitlist',
}

export type BookingEntry = {
  type: UpcomingEntryType.BOOKING;
  data: OrderData;
};

export type WaitlistEntry = {
  type: UpcomingEntryType.WAITLIST;
  data: WaitingListItemData;
};

export type UpcomingEntry = BookingEntry | WaitlistEntry;

type BookingCardProps = {
  entry: UpcomingEntry;
  account: AccountData;
  onCancel?: () => void;
  onClick?: () => void;
  muted?: boolean;
};

export const BookingCard = ({ entry, account, onCancel, onClick, muted = false }: BookingCardProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const { employeeIdToName, orders } = useManagePasses();
  const { longDateFormat } = getDateFormat(account);
  const timeFormat = getTimeFormat(account);

  const isWaitlist = entry.type === UpcomingEntryType.WAITLIST;
  const orderPageId = entry.data.order_page_id;
  const experienceName = getExperienceName(account, orderPageId, language);
  const { date } = entry.data;

  const order = entry.type === UpcomingEntryType.BOOKING ? entry.data : null;
  const employeeName = order?.employee_ids?.[0] ? employeeIdToName[order.employee_ids[0]] : undefined;
  const hasGuest = !!order?.guest_order_id;
  const guestOrder = hasGuest ? orders.find((o) => o.id === order.guest_order_id) : undefined;

  return (
    <Grid
      container
      flexDirection="column"
      gap={1}
      onClick={onClick}
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: 2,
        p: isSmallScreen ? 2 : 2.5,
        opacity: muted ? 0.6 : 1,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        ...(onClick && { cursor: 'pointer' }),
        ...(isWaitlist && { border: '2px solid #e6a800' }),
      }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Typography variant="body1" fontWeight={600}>
          {experienceName}
        </Typography>
        <Grid item flexDirection="column" alignItems="flex-end">
          {isWaitlist && (
            <Chip
              label={t('manage_passes.schedule.statusWaitlist')}
              size="small"
              sx={{
                color: '#8a6d00',
                backgroundColor: '#fff8e1',
                fontWeight: 500,
              }}
            />
          )}
          {order && order.persons > 1 && (
            <Typography variant="body2" color="text.secondary">
              {order.persons} {t('manage_passes.booking.persons')}
            </Typography>
          )}
          {employeeName && (
            <Typography variant="body2" color="text.secondary">
              {employeeName}
            </Typography>
          )}
        </Grid>
      </Grid>
      {hasGuest && (
        <Grid container alignItems="center" gap={0.5}>
          <PeopleIcon sx={{ fontSize: 16, color: '#71B266' }} />
          <Typography variant="body2" fontWeight={500} sx={{ color: '#71B266' }}>
            {guestOrder
              ? t('manage_passes.schedule.guestBadge', { name: guestOrder.firstname })
              : t('manage_passes.schedule.guestInvited')}
          </Typography>
        </Grid>
      )}
      <Grid container justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {date ? moment(date).format(longDateFormat) : ''}{' '}
          {order?.time !== undefined
            ? `${moment.utc(order.time * 1000).format(timeFormat)} - ${moment
                .utc((order.time + order.duration) * 1000)
                .format(timeFormat)}`
            : ''}
        </Typography>
        <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 'auto' }}>
          {onClick && (
            <IconButton
              size="small"
              color="primary"
              aria-label={t('manage_passes.qrCheckin.title')}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onClick();
              }}
              sx={{
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: 1.5,
                p: 0.5,
              }}>
              <QrCode2Icon fontSize="small" />
            </IconButton>
          )}
          {onCancel && (
            <Button
              variant="outlined"
              size="small"
              color={isWaitlist ? 'warning' : 'error'}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onCancel();
              }}
              sx={{ minHeight: 32 }}>
              {isWaitlist ? t('manage_passes.schedule.leaveWaitlist') : t('manage_passes.booking.cancel')}
            </Button>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
